import { api, unwrapArray, unwrapObject } from "@/lib/api";

/**
 * Push notifications on this device. The CRM's own Firebase project does the delivery; the browser needs its public
 * web config and Web Push key (from the CRM), asks the person's permission, gets a token for this device and gives it
 * to the CRM. Firebase's code is loaded only when someone turns push on (or this device already has it on).
 */

const TOKEN_KEY = "crm.push.token";
const USER_KEY = "crm.push.user";
const SYNCED_KEY = "crm.push.synced";
/** A person who turned push off on purpose here: never turned on again by itself */
const offKey = (userId: string) => `crm.push.off.${userId}`;
/** When the banner was last put off with "Not now" */
const LATER_KEY = "crm.push.later";
const LATER_MS = 7 * 24 * 3600 * 1000;
/** Tell the CRM this device is still here at most this often */
const RESYNC_MS = 24 * 3600 * 1000;

export type PushState =
  | "unsupported" // this browser can't do push at all
  | "needs-install" // iPhone / iPad: only works once the CRM is added to the Home Screen
  | "not-configured" // an admin hasn't set up Firebase yet
  | "denied" // the person blocked notifications in the browser
  | "off" // available, not turned on here
  | "on";

interface PushConfig {
  enabled: boolean;
  web_config?: Record<string, string>;
  vapid_key?: string;
}

const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private window */ } },
  del: (k: string) => { try { localStorage.removeItem(k); } catch { /* private window */ } },
};

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;

export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** "Chrome on Windows", "Safari on iPhone": a name the person will recognise in their list of devices */
export function deviceName() {
  const ua = navigator.userAgent;
  const browser = /edg\//i.test(ua) ? "Edge" : /opr\//i.test(ua) ? "Opera" : /firefox|fxios/i.test(ua) ? "Firefox" : /chrome|crios/i.test(ua) ? "Chrome" : /safari/i.test(ua) ? "Safari" : "Browser";
  const os = /android/i.test(ua) ? "Android" : /iphone/i.test(ua) ? "iPhone" : /ipad/i.test(ua) ? "iPad" : /windows/i.test(ua) ? "Windows" : /mac os/i.test(ua) ? "Mac" : /linux|cros/i.test(ua) ? "Linux" : "";
  return `${browser}${os ? ` on ${os}` : ""}`;
}

export const thisDeviceToken = () => store.get(TOKEN_KEY);

async function loadConfig(): Promise<PushConfig> {
  return unwrapObject<PushConfig>(await api.get("/api/Push/Config"));
}

/** What this device can do right now, for the settings panel */
export async function pushState(): Promise<PushState> {
  if (isIos() && !isStandalone()) return "needs-install";
  if (!pushSupported()) return "unsupported";
  let cfg: PushConfig;
  try { cfg = await loadConfig(); } catch { return "not-configured"; }
  if (!cfg.enabled) return "not-configured";
  if (Notification.permission === "denied") return "denied";
  return Notification.permission === "granted" && !!thisDeviceToken() ? "on" : "off";
}

async function serviceWorker() {
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
}

async function messagingFor(cfg: PushConfig, reg: ServiceWorkerRegistration) {
  const [{ initializeApp, getApps, getApp }, { getMessaging, getToken }] = await Promise.all([import("firebase/app"), import("firebase/messaging")]);
  const app = getApps().length ? getApp() : initializeApp(cfg.web_config as any);
  const token = await getToken(getMessaging(app), { vapidKey: cfg.vapid_key, serviceWorkerRegistration: reg });
  if (!token) throw new Error("The browser didn't give this device a push address. Try again.");
  return token;
}

async function register(token: string, userId: string) {
  await api.post("/api/Push/Devices", { token, platform: "web", device_name: deviceName() });
  store.set(TOKEN_KEY, token);
  store.set(USER_KEY, userId);
  store.set(SYNCED_KEY, String(Date.now()));
}

/** Turns push on for this device: asks permission, gets the device's token, gives it to the CRM */
export async function enablePush(userId: string): Promise<void> {
  if (isIos() && !isStandalone()) throw new Error("On an iPhone or iPad, add the CRM to your Home Screen first, then open it from there and turn notifications on.");
  if (!pushSupported()) throw new Error("This browser can't receive push notifications.");
  const cfg = await loadConfig();
  if (!cfg.enabled) throw new Error("Push isn't set up yet. A Super Admin can do that in Settings → Push notifications.");
  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications are blocked for this site. Allow them in the browser's site settings, then try again.");
  const reg = await serviceWorker();
  await register(await messagingFor(cfg, reg), userId);
  store.del(offKey(userId));
}

/** Turns push off for this device (the CRM forgets it and the browser drops its token) */
export async function disablePush(userId?: string): Promise<void> {
  if (userId) store.set(offKey(userId), "1");
  const token = thisDeviceToken();
  if (token) await api.post("/api/Push/Devices/Remove", { token }).catch(() => undefined);
  try {
    const { getApps, getApp } = await import("firebase/app");
    if (getApps().length) {
      const { getMessaging, deleteToken } = await import("firebase/messaging");
      await deleteToken(getMessaging(getApp()));
    }
  } catch { /* nothing to drop */ }
  store.del(TOKEN_KEY);
  store.del(USER_KEY);
  store.del(SYNCED_KEY);
}

/** Signing out: this device stops getting this person's pushes. It is turned on again at their next sign-in. */
export async function pauseOnSignOut(): Promise<void> {
  const token = thisDeviceToken();
  if (!token) return;
  await api.post("/api/Push/Devices/Remove", { token }).catch(() => undefined);
  store.del(TOKEN_KEY);
  store.del(SYNCED_KEY);
}

/** The banner was put off with "Not now" */
export const snoozePrompt = () => store.set(LATER_KEY, String(Date.now()));

/** "Don't ask again" (or turning it off in settings): this person's pushes are never turned on here by themselves */
export const stopAsking = (userId: string) => store.set(offKey(userId), "1");

/**
 * What to do about notifications for this person on this device, at sign-in:
 *  - push is set up and the browser already allows notifications → register the device quietly (and keep it fresh)
 *  - never asked → "ask" (push is set up) or "ask-calls" (only incoming-call pop-ups apply): show the banner
 *  - blocked, unsupported, or the person said no → nothing
 * A permission box is never opened without a click: Firefox and Safari refuse it, and Chrome starts hiding the box for
 * the whole site when people ignore a surprise one.
 */
export async function autoPush(userId: string, canCall: boolean): Promise<"ask" | "ask-calls" | "none"> {
  if (typeof window === "undefined" || !("Notification" in window)) return "none";
  if (isIos() && !isStandalone()) return "none";
  if (store.get(offKey(userId)) || Notification.permission === "denied") return "none";
  const waited = Date.now() - Number(store.get(LATER_KEY) || 0) < LATER_MS;
  let cfg: PushConfig = { enabled: false };
  try { cfg = await loadConfig(); } catch { /* push isn't available */ }
  const push = cfg.enabled && pushSupported();
  if (Notification.permission === "granted") {
    let fresh = !!store.get(TOKEN_KEY) && store.get(USER_KEY) === userId && Date.now() - Number(store.get(SYNCED_KEY) || 0) < RESYNC_MS;
    // The CRM may have forgotten this device since (the password was changed, an old device was cleaned up): then sign it up again
    const mine = store.get(TOKEN_KEY);
    if (push && fresh && mine) {
      try { fresh = unwrapArray<{ token_tail: string }>(await api.get("/api/Push/Devices")).some((d) => mine.endsWith(d.token_tail)); } catch { /* keep going */ }
    }
    if (push && !fresh) {
      try { await register(await messagingFor(cfg, await serviceWorker()), userId); } catch { /* try again at the next sign-in */ }
    }
    return "none";
  }
  if (waited) return "none";
  return push ? "ask" : canCall ? "ask-calls" : "none";
}

/** The "Turn on" button of the banner when only incoming-call pop-ups apply (no Firebase): just the browser's permission box */
export async function allowCallPopups(): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notifications are blocked for this site. Allow them in the browser's site settings to get pop-ups for incoming calls.");
}

// ---------------------------------------------------------------------------
// Pop-ups and the app icon's number: kept in step with what has been read
// ---------------------------------------------------------------------------

/** The name a notification's pop-up has on the device (set by the CRM when it sends the push) */
export const popupTag = (n: { id: string; group_key?: string | null }) => (n.group_key ? `g-${n.group_key}` : `n-${n.id}`);

async function openPopups(): Promise<globalThis.Notification[]> {
  try {
    const reg = typeof navigator !== "undefined" && "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
    return reg ? await reg.getNotifications() : [];
  } catch {
    return [];
  }
}

/** The person read these in the CRM: their pop-ups on this device go away */
export async function closePopupsFor(rows: Array<{ id: string; group_key?: string | null }>): Promise<void> {
  const tags = new Set(rows.map(popupTag));
  (await openPopups()).filter((p) => tags.has(p.tag)).forEach((p) => p.close());
}

/** "Mark all read": every CRM pop-up on this device goes away */
export async function closeAllPopups(): Promise<void> {
  (await openPopups()).forEach((p) => p.close());
}

let lastSync = 0;
/**
 * Pop-ups left on this device for things already read somewhere else (the laptop, the phone) are cleared the next time
 * the CRM is opened here. Looks at the latest notifications; a pop-up it can't match is left alone.
 */
export async function syncPopups(): Promise<void> {
  if (Date.now() - lastSync < 10000) return;
  lastSync = Date.now();
  const open = (await openPopups()).filter((p) => p.tag && p.tag !== "TEST");
  if (!open.length) return;
  let rows: Array<{ id: string; group_key?: string | null; read_at?: string | null }>;
  try {
    rows = unwrapArray(await api.get("/api/Notification/List?limit=100"));
  } catch {
    return;
  }
  for (const p of open) {
    if (p.tag.startsWith("n-")) {
      const row = rows.find((r) => r.id === p.tag.slice(2));
      if (row?.read_at) p.close();
    } else if (p.tag.startsWith("g-")) {
      const same = rows.filter((r) => r.group_key === p.tag.slice(2));
      if (same.length && same.every((r) => r.read_at)) p.close();
    }
  }
}

/** The number on the app's icon (phone Home Screen, installed desktop app); a browser tab without that support ignores it */
export function setBadge(count: number): void {
  try {
    const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (count > 0) void nav.setAppBadge?.(count)?.catch(() => undefined);
    else void nav.clearAppBadge?.()?.catch(() => undefined);
  } catch { /* not supported */ }
}
